import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useRememberedState } from '../hooks/useRemembered';
import {
    Auftragsbestand,
    KAPAZITAET_FREELANCER_DEFAULT,
    KAPAZITAET_KOORDINATOR_DEFAULT,
    Kapazitaet,
    Liquiditaet,
    Nachkalkulation,
    NachkalkulationKunde,
    berechneAuftragsbestand,
    berechneKapazitaet,
    berechneLiquiditaet,
    berechneNachkalkulation,
} from '../services/erpSteering';
import {
    EffortRate,
    ProjectOrderValue,
    fetchAuftragsbestand,
    fetchEffortRates,
    fetchKapazitaetsBasis,
    fetchKundenStamm,
    fetchLiquiditaetsBelege,
    fetchNachkalkulation,
    fetchOrderValues,
} from '../services/erpSteeringService';
import { PlzMapping, RegionLevel, regionKeyForZip } from '../services/crmMapData';
import { ladePlzMapping } from './useCrmMap';
import { describeError } from '../services/errorMessage';

// Ladeschicht des ERP-Cockpits (ERP/CRM-Plan Abschnitt 8 — Phase 5).
//
// Hier steckt das IO und der Zustand; die Rechnung steht pur in
// services/erpSteering.ts, das Laden in services/erpSteeringService.ts. Die
// Komponenten darunter sind duenne Container (Standing Decision).
//
// JE BEREICH EIGENSTAENDIG geladen: das Cockpit hat vier Auswertungen, die
// zusammen jede Rechnung, jede Gutschrift und jedes Projekt beruehren. Sie
// alle beim Oeffnen zu ziehen waere ein spuerbarer Ladehaenger fuer eine
// Ansicht, von der man meist nur eine Karte braucht. Jeder Bereich laedt beim
// ersten Betreten und merkt sich, dass er geladen hat.

export type ErpBereich =
    | 'auftragsbestand'
    | 'nachkalkulation'
    | 'kapazitaet'
    | 'liquiditaet'
    | 'auftragswerte'
    | 'saetze';

interface Ladezustand<T> {
    daten: T | null;
    laedt: boolean;
    fehler: string | null;
}

const leer = <T,>(): Ladezustand<T> => ({ daten: null, laedt: false, fehler: null });

export interface NachkalkulationFilter {
    von: string;
    bis: string;
    kundeId: string;
    branche: string;
}

export interface KapazitaetEinstellungen {
    monate: number;
    kapazitaetKoordinator: number;
    kapazitaetFreelancer: number;
}

/** Erster Tag des Monats, der `monate` Monate vor `heute` liegt. */
const monateZurueck = (heute: string, monate: number): string => {
    const jahr = Number(heute.slice(0, 4));
    const m = Number(heute.slice(5, 7));
    return new Date(Date.UTC(jahr, m - 1 - monate, 1)).toISOString().slice(0, 10);
};

export const useErpCockpit = (heute: string) => {
    const [bereich, setBereich] = useRememberedState<ErpBereich>('erp.bereich', 'auftragsbestand');

    const [bestand, setBestand] = useState<Ladezustand<Auftragsbestand>>(leer);
    const [nachkalk, setNachkalk] = useState<Ladezustand<Nachkalkulation>>(leer);
    const [kapazitaet, setKapazitaet] = useState<Ladezustand<Kapazitaet>>(leer);
    const [liquiditaet, setLiquiditaet] = useState<Ladezustand<Liquiditaet>>(leer);
    const [auftragswerte, setAuftragswerte] = useState<Ladezustand<ProjectOrderValue[]>>(leer);
    const [saetze, setSaetze] = useState<Ladezustand<EffortRate[]>>(leer);

    const [kunden, setKunden] = useState<NachkalkulationKunde[]>([]);
    const [plzMapping, setPlzMapping] = useState<PlzMapping | null>(null);
    const [regionLevel] = useState<RegionLevel>('bundesland');
    const [kapazitaetsWarnungen, setKapazitaetsWarnungen] = useState<string[]>([]);

    const [filter, setFilter] = useState<NachkalkulationFilter>(() => ({
        von: monateZurueck(heute, 12),
        bis: heute,
        kundeId: '',
        branche: '',
    }));
    const [einstellungen, setEinstellungen] = useState<KapazitaetEinstellungen>({
        monate: 12,
        kapazitaetKoordinator: KAPAZITAET_KOORDINATOR_DEFAULT,
        kapazitaetFreelancer: KAPAZITAET_FREELANCER_DEFAULT,
    });

    // Welche Bereiche schon einmal geladen wurden. Ref und nicht State: eine
    // Aenderung daran darf keinen Renderlauf ausloesen.
    const geladen = useRef<Set<ErpBereich>>(new Set());

    // Die PLZ-Zuordnung wird von Nachkalkulation UND Kapazitaet gebraucht;
    // `ladePlzMapping` cacht das Ergebnis modulweit (useCrmMap), der Aufruf
    // hier kostet also hoechstens beim ersten Mal.
    useEffect(() => {
        let abgebrochen = false;
        ladePlzMapping()
            .then((m) => { if (!abgebrochen) setPlzMapping(m); })
            .catch(() => { /* ohne Mapping bleibt die Region „unbekannt" */ });
        return () => { abgebrochen = true; };
    }, []);

    const ladeBestand = useCallback(async () => {
        setBestand({ daten: null, laedt: true, fehler: null });
        try {
            const rows = await fetchAuftragsbestand();
            setBestand({ daten: berechneAuftragsbestand(rows), laedt: false, fehler: null });
        } catch (e) {
            setBestand({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, []);

    const ladeNachkalkulation = useCallback(async () => {
        setNachkalk({ daten: null, laedt: true, fehler: null });
        try {
            const [rows, stamm] = await Promise.all([
                fetchNachkalkulation(filter.von || null, filter.bis || null),
                // Der Kundenstamm aendert sich waehrend einer Sitzung selten;
                // er wird trotzdem mitgeladen, weil eine neu gepflegte Branche
                // sonst erst nach einem Neuladen der Seite wirkt.
                fetchKundenStamm().catch(() => [] as NachkalkulationKunde[]),
            ]);
            setKunden(stamm);
            setNachkalk({
                daten: berechneNachkalkulation(rows, stamm, { plzMapping, regionLevel }),
                laedt: false,
                fehler: null,
            });
        } catch (e) {
            setNachkalk({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, [filter.von, filter.bis, plzMapping, regionLevel]);

    const ladeKapazitaet = useCallback(async () => {
        setKapazitaet({ daten: null, laedt: true, fehler: null });
        try {
            const basis = await fetchKapazitaetsBasis();
            setKapazitaetsWarnungen(basis.warnungen);
            // Region des Projekts aus seiner Baustellen-PLZ. Bevorzugt das
            // Bundeslandkuerzel (lesbar), notfalls der AGS; ohne Treffer
            // „unbekannt" — dieselbe Regel wie auf der Karte.
            const projekte = basis.projekte.map((p) => {
                const treffer = regionKeyForZip(p.zip, plzMapping, regionLevel);
                return { ...p, region: treffer?.bundeslandKuerzel || treffer?.key || 'unbekannt' };
            });
            setKapazitaet({
                daten: berechneKapazitaet(
                    projekte,
                    basis.assignments,
                    basis.koordinatoren.map((k) => ({
                        ...k,
                        begehungenProMonat: einstellungen.kapazitaetKoordinator,
                    })),
                    {
                        heute,
                        monate: einstellungen.monate,
                        freelancer: basis.freelancer.map((f) => ({
                            ...f,
                            begehungenProMonat: einstellungen.kapazitaetFreelancer,
                        })),
                    },
                ),
                laedt: false,
                fehler: null,
            });
        } catch (e) {
            setKapazitaet({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, [heute, plzMapping, regionLevel, einstellungen]);

    const ladeLiquiditaet = useCallback(async () => {
        setLiquiditaet({ daten: null, laedt: true, fehler: null });
        try {
            const { rechnungen, gutschriften } = await fetchLiquiditaetsBelege();
            setLiquiditaet({
                daten: berechneLiquiditaet(rechnungen, gutschriften, heute),
                laedt: false,
                fehler: null,
            });
        } catch (e) {
            setLiquiditaet({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, [heute]);

    const ladeAuftragswerte = useCallback(async () => {
        setAuftragswerte({ daten: null, laedt: true, fehler: null });
        try {
            setAuftragswerte({ daten: await fetchOrderValues(), laedt: false, fehler: null });
        } catch (e) {
            setAuftragswerte({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, []);

    const ladeSaetze = useCallback(async () => {
        setSaetze({ daten: null, laedt: true, fehler: null });
        try {
            setSaetze({ daten: await fetchEffortRates(), laedt: false, fehler: null });
        } catch (e) {
            setSaetze({ daten: null, laedt: false, fehler: describeError(e) });
        }
    }, []);

    const lader = useMemo(
        (): Record<ErpBereich, () => Promise<void>> => ({
            auftragsbestand: ladeBestand,
            nachkalkulation: ladeNachkalkulation,
            kapazitaet: ladeKapazitaet,
            liquiditaet: ladeLiquiditaet,
            auftragswerte: ladeAuftragswerte,
            saetze: ladeSaetze,
        }),
        [ladeBestand, ladeNachkalkulation, ladeKapazitaet, ladeLiquiditaet, ladeAuftragswerte, ladeSaetze],
    );

    useEffect(() => {
        if (geladen.current.has(bereich)) return;
        geladen.current.add(bereich);
        void lader[bereich]();
        // Absichtlich nur an `bereich` gebunden: die Lader-Identitaeten aendern
        // sich mit Filtern und Einstellungen, und dieser Effekt soll NICHT bei
        // jeder Filteraenderung neu feuern (dafuer gibt es `neuLaden`).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bereich]);

    /** Aktuellen Bereich erneut laden (Filter geaendert, Wert gespeichert). */
    const neuLaden = useCallback(() => {
        geladen.current.add(bereich);
        void lader[bereich]();
    }, [bereich, lader]);

    return {
        bereich,
        setBereich,
        bestand,
        nachkalk,
        kapazitaet,
        liquiditaet,
        auftragswerte,
        saetze,
        kunden,
        plzMapping,
        kapazitaetsWarnungen,
        filter,
        setFilter,
        einstellungen,
        setEinstellungen,
        neuLaden,
    };
};
